import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import appInsightsService from './services/appInsights';
import { getAppInsightsConnectionString, getAppInsightsInstrumentationKey, getServerUrl } from './config/runtimeConfig';

// Global error handler to suppress noisy Application Insights errors
const originalError = console.error;
console.error = (...args) => {
  const message = args.join(' ');
  
  // Suppress Application Insights internal Ajax monitoring errors
  if (message.includes('Failed to monitor XMLHttpRequest') ||
      message.includes('Failed to calculate the duration of the ajax call') ||
      message.includes('Maximum ajax per page view limit reached') ||
      message.includes('AI (Internal)')) {
    // These are internal Application Insights errors that don't affect functionality
    return;
  }
  
  // Call original console.error for all other errors
  originalError.apply(console, args);
};

// Global window error handler for unhandled errors
window.addEventListener('error', (event) => {
  const message = event.error?.message || event.message || '';
  
  // Suppress Application Insights internal errors
  if (message.includes('Failed to monitor XMLHttpRequest') ||
      message.includes('Failed to calculate the duration of the ajax call') ||
      message.includes('Maximum ajax per page view limit reached') ||
      message.includes('AI (Internal)')) {
    event.preventDefault();
    event.stopPropagation();
    return false;
  }
});

// Global unhandled promise rejection handler
window.addEventListener('unhandledrejection', (event) => {
  const message = event.reason?.message || event.reason || '';
  
  // Suppress Application Insights internal errors
  if (typeof message === 'string' && (
      message.includes('Failed to monitor XMLHttpRequest') ||
      message.includes('Failed to calculate the duration of the ajax call') ||
      message.includes('Maximum ajax per page view limit reached') ||
      message.includes('AI (Internal)'))) {
    event.preventDefault();
    return false;
  }
});

// Enhanced Application Insights initialization with runtime configuration
const initializeApplicationInsights = async () => {
  try {
    // First, try environment variables (build-time configuration)
    const connectionString = getAppInsightsConnectionString();
    const instrumentationKey = getAppInsightsInstrumentationKey();
    const isDevelopment = process.env.NODE_ENV === 'development' || process.env.REACT_APP_NODE_ENV === 'development';
    
    console.log('🔧 Application Insights Initialization Started');
    console.log('Environment Configuration:', {
      hasConnectionString: !!connectionString,
      hasInstrumentationKey: !!instrumentationKey,
      environment: isDevelopment ? 'Development' : 'Production',
      currentTime: new Date().toISOString()
    });

    // Validate environment configuration
    const hasValidKey = instrumentationKey && 
                        instrumentationKey !== '00000000-0000-0000-0000-000000000000' &&
                        /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(instrumentationKey);
    
    const hasValidConnectionString = connectionString && 
                                    !connectionString.includes('00000000-0000-0000-0000-000000000000') &&
                                    connectionString.includes('InstrumentationKey=');

    let initResult = false;
    let configSource = 'none';

    // Try environment variables first
    if (hasValidConnectionString) {
      console.log('✅ Using environment connection string');
      initResult = appInsightsService.initialize(connectionString);
      configSource = 'environment-connectionstring';
    } else if (hasValidKey) {
      console.log('✅ Using environment instrumentation key');
      initResult = appInsightsService.initialize(instrumentationKey);
      configSource = 'environment-key';
    }

    // If environment config failed, try runtime configuration from backend
    if (!initResult && !isDevelopment) {
      console.log('🔄 Environment config not available, trying runtime configuration...');
      
      try {
        // Get server URL (try different common patterns)
        const serverUrl = getServerUrl();
        
        console.log('📡 Fetching Application Insights config from:', `${serverUrl}/app-insights/config`);
        
        const response = await fetch(`${serverUrl}/app-insights/config`, {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          },
          credentials: 'include'
        });

        if (response.ok) {
          const config = await response.json();
          console.log('📥 Runtime configuration received:', {
            configured: config.configured,
            method: config.method,
            hasConnectionString: !!config.connectionString,
            hasInstrumentationKey: !!config.instrumentationKey
          });

          if (config.configured) {
            if (config.connectionString) {
              initResult = appInsightsService.initialize(config.connectionString);
              configSource = 'runtime-connectionstring';
            } else if (config.instrumentationKey) {
              initResult = appInsightsService.initialize(config.instrumentationKey);
              configSource = 'runtime-key';
            }
          } else {
            console.warn('⚠️ Backend reports no Application Insights configuration available');
          }
        } else {
          console.warn('⚠️ Failed to fetch runtime configuration:', response.status, response.statusText);
        }
      } catch (fetchError) {
        console.warn('⚠️ Error fetching runtime configuration:', fetchError.message);
      }
    }

    // If still no configuration and in development, use development mode
    if (!initResult && isDevelopment) {
      console.log('🔧 Using development mode (console logging only)');
      initResult = appInsightsService.initializeForDevelopment();
      configSource = 'development-mode';
    }

    // Final status logging
    console.log('🔍 Application Insights Final Status:', {
      initialized: appInsightsService.initialized,
      initializationResult: initResult,
      configurationSource: configSource,
      hasAppInsights: !!appInsightsService.appInsights,
      hasCore: !!appInsightsService.appInsights?.core,
      environment: isDevelopment ? 'Development' : 'Production'
    });

    // Test tracking if successfully initialized
    if (appInsightsService.initialized && configSource !== 'development-mode') {
      appInsightsService.trackTrace('Frontend Application Insights initialized successfully', 1, {
        environment: isDevelopment ? 'Development' : 'Production',
        configurationSource: configSource,
        initTime: new Date().toISOString(),
        userAgent: navigator.userAgent.substring(0, 100),
        url: window.location.href
      });
      
      // Track the initialization event
      appInsightsService.trackEvent('ApplicationInsights:Initialized', {
        configurationSource: configSource,
        environment: isDevelopment ? 'Development' : 'Production',
        success: true
      });
      
      console.log('✅ Application Insights telemetry test sent');
    } else if (configSource === 'development-mode') {
      console.log('🔧 Development mode active - logs will appear in console only');
    } else {
      console.error('❌ Application Insights initialization failed');
    }

    return initResult;
  } catch (error) {
    console.error('❌ Application Insights initialization error:', error);
    return false;
  }
};

// Initialize Application Insights before rendering
const startApp = async () => {
  try {
    await initializeApplicationInsights();
  } catch (error) {
    console.error('App Insights initialization failed, but continuing with app startup:', error);
  }

  // Render the app
  const root = ReactDOM.createRoot(document.getElementById('root'));
  root.render(
    <React.StrictMode>
      <App />
    </React.StrictMode>
  );

  // Create logger that sends to both console and App Insights
  // const logger = appInsightsService.getLogger('index');

  // Expose appInsightsService for testing/debugging purposes
  window.appInsightsService = appInsightsService;

  // Set up web vitals tracking with error handling
  try {
    reportWebVitals((metric) => {
      try {
        const name = metric.name || metric.id;
        const value = metric.value || metric.delta;
        const label = metric.label || 'unknown';
        
        console.log(`📊 Web Vital captured: ${name} (${label}) = ${value}`);
        
        if (appInsightsService.initialized) {
          appInsightsService.trackMetric(`WebVital:${name}`, value, {
            label: label,
            url: window.location.href,
            component: 'web-vitals'
          });
        }
      } catch (err) {
        console.warn('Failed to track web vital metric:', err);
      }
    });
  } catch (err) {
    console.warn('Web vitals reporting disabled due to error:', err);
  }

  console.log('🚀 Application startup completed');
};

// Start the application
startApp();

//<link rel="icon" href="%PUBLIC_URL%/midiPlayer" />
